import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import type { RootState } from "../../common/state/store";
import {
  duplicateProject,
  getCurrentProject,
  getEditedProject,
  getNewProjectSelector,
  makeTemplate,
  setCurrentProject,
  updateProject,
} from "../../features/projects/state/projectsSlice";
import {
  emptyOrganization,
  getCurrentOrganization,
} from "../../features/profile/state/profileSlice";
import { DynamicMedia } from "../../features/graph/components/DynamicField";
import { useMediaById } from "../../features/gallery/useMediaById";
import { useEffect, useState } from "react";
import type { IMedia } from "../../model/unityObject";
import { useMediaPicker } from "../../features/gallery/picker/useMediaPicker";
import {
  addCurrentElement,
  fetchAllCurrentElements,
  fetchSpecificProjectGroupWithProjects,
  getCurrentElements,
  getSelectedProjectGroup,
  refreshProjectGroupIfSelected,
  setCurrentElements,
  setSelectedProjectGroupFromProjectGroupId,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { IsLoading, useLoading } from "../../common/components/AppState/IsLoading";
import { LoadingState } from "../../common/state/slice/appStateSlice";
import { useTourView } from "../../features/onboarding/engine/useTourView";
import { TOUR_VIEW_ANCHORS } from "../../features/onboarding/engine/tourViewAnchors";
import { FooterButtons } from "../../common/components/FooterButtons";
import { PROJECT_LIST_COMPONENT_ID } from "../../features/projects/components/ProjectList";
import { PROJECT_LIST_COMPONENT_CLONE_LOADING_ID } from "./ProjectsList";
import { setAlert } from "../../features/alert/state/alertsSlice";
import { loadGraph } from "src/features/graph/state/graphLegacy";
import { trackEvent } from "../../trackEvent";

interface IProjectThumbnail {
  decrementStep?: () => void;
  mode?: string;
  onCancelClick?: (() => void) | null;
}

interface IThumbnailFormValues {
  thumbnailId: number;
  source_thumbnail: number;
}

const DEFAULT_THUMBNAIL_ID = 47;

const ProjectThumbnail = ({
  decrementStep,
  mode = "",
  onCancelClick = null,
}: IProjectThumbnail) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const path = location.pathname.split("/");
  const cloneLoadingState = useLoading(PROJECT_LIST_COMPONENT_CLONE_LOADING_ID);
  useTourView(
    "module-wizard-thumbnail",
    cloneLoadingState !== LoadingState.Loading,
    mode === "createProject" ? TOUR_VIEW_ANCHORS["module-wizard-thumbnail"] : [],
  );

  const { register, handleSubmit, setValue, control } = useForm<IThumbnailFormValues>();

  const selectedProjectElements = useAppSelector(getCurrentElements);
  const currentOrganization = useAppSelector(getCurrentOrganization) ?? emptyOrganization();

  const { newProject, currentProjectGroup, editedProject, currentProject } = useAppSelector(
    (state: RootState) => {
      return {
        newProject: getNewProjectSelector(state),
        currentProjectGroup: getSelectedProjectGroup(state),
        currentProject: getCurrentProject(state),
        editedProject: getEditedProject(state),
      };
    },
  );

  const initialThumbnailId = (): number => {
    const projectThumbnailId = Number(currentProject?.source_thumbnail?.id) || 0;
    if (mode === "edit-thumbnail") return projectThumbnailId || DEFAULT_THUMBNAIL_ID;
    if (mode === "faitTemplate" || mode === "duplicateProject") {
      return newProject.thumbnailId > 0
        ? Number(newProject.thumbnailId)
        : projectThumbnailId || DEFAULT_THUMBNAIL_ID;
    }
    return DEFAULT_THUMBNAIL_ID;
  };

  const [currentThumbnail, setCurrentThumbnail] = useState<number>(initialThumbnailId);
  const {
    media: currentThumbnailMedia,
    status: currentThumbnailStatus,
    retry: retryCurrentThumbnail,
  } = useMediaById(currentThumbnail > 0 ? currentThumbnail : undefined);
  const isThumbnailLoading =
    currentThumbnail > 0 && !currentThumbnailMedia && currentThumbnailStatus === "loading";
  const isThumbnailLoadError = !currentThumbnailMedia && currentThumbnailStatus === "error";

  useEffect(() => {
    if (mode === "edit-thumbnail") {
      const tbnId = Number(currentProject.source_thumbnail.id);
      setValue("thumbnailId", tbnId);
      setCurrentThumbnail(tbnId);
    } else if (mode === "faitTemplate" || mode === "duplicateProject") {
      if (newProject.thumbnailId > 0) {
        const tbnId = Number(newProject.thumbnailId);

        setValue("thumbnailId", tbnId);

        setCurrentThumbnail(tbnId);
      } else {
        const tbnId = Number(currentProject.source_thumbnail.id);

        setValue("thumbnailId", tbnId);

        setCurrentThumbnail(tbnId);
      }
    }
  }, [currentProject.source_thumbnail]);

  // Dance around the media preview issues that arose from repurposing the "EditNodeForm" component
  const getDefaultTbn = () => {
    const enhancedField = {
      type: "media",
      mediaType: "image",
      fieldName: "source_thumbnail",
      currentValue: currentThumbnail,
      relevantSourceMedia: currentThumbnailMedia,
    };
    if (!currentThumbnail) {
      enhancedField.currentValue = -1;
      enhancedField.relevantSourceMedia = currentThumbnailMedia;
    }
    return enhancedField;
  };

  const { pickMedia, pickerElement } = useMediaPicker();

  const onMediaChange = (selectedMedia: IMedia) => {
    setCurrentThumbnail(Number(selectedMedia.id));
  };

  const openGallery = async () => {
    const media = await pickMedia({
      allowedMediaTypes: "image",
      initialSearch: { mediaType: "image", media360tag: "" },
      initialMediaId: currentThumbnail,
    });
    if (media) onMediaChange(media);
  };

  const onSubmit = () => {};

  const enhancedField = getDefaultTbn();

  useEffect(() => {
    setValue("source_thumbnail", currentThumbnail);
  }, [currentThumbnail]);

  return (
    <>
      {pickerElement}
      <IsLoading
        componentId={PROJECT_LIST_COMPONENT_CLONE_LOADING_ID}
        showSuccess={false}
        spinnerPlaceholder
      >
        <h1 className="text-2xl my-5 px-6 font-medium">{t("general.selectThumbnail")}</h1>

        <form
          className="flex flex-col grow w-full justify-between"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-10 w-full my-5 px-6">
            <div
              className="flex flex-col items-center justify-center bg-green-200 p-6 w-full max-w-max"
              data-tour={mode === "createProject" ? "module-wizard-thumbnail" : undefined}
            >
              <Controller
                control={control}
                name={"source_thumbnail"}
                render={({ field }) => (
                  <DynamicMedia
                    field={{
                      onChange: field.onChange,
                      onBlur: field.onBlur,
                      value: field.value,
                      name: field.name,
                      ref: field.ref,
                    }}
                    fieldType={enhancedField.type}
                    register={register}
                    handleButtonClick={() => {
                      void openGallery();
                    }}
                    currentMedia={enhancedField.currentValue}
                    media={enhancedField.relevantSourceMedia as IMedia}
                    isLoading={isThumbnailLoading}
                    loadError={isThumbnailLoadError}
                    onRetryLoad={retryCurrentThumbnail}
                  />
                )}
              />
            </div>
          </div>

          <FooterButtons
            cancelText={t("general.cancel")}
            cancelCb={() => {
              if (onCancelClick) {
                onCancelClick();
              } else {
                if (mode === "edit-thumbnail" || mode === "faitTemplate") {
                  if (path[1] === "templates") {
                    navigate("/templates");
                  } else {
                    navigate(
                      path[path.length - 2] === "graph"
                        ? `/courses/${currentProjectGroup.id}/list/${currentProject.id}/graph`
                        : `/courses/${currentProjectGroup.id}/list`,
                    );
                  }
                  return;
                }

                navigate(`/courses/${currentProjectGroup.id}/list/${currentProject.id}/graph`);
              }
            }}
            backText={decrementStep ? t("general.back") : undefined}
            backCb={decrementStep ? () => decrementStep() : undefined}
            nextText={mode === "faitTemplate" ? t("general.create") : t("general.save")}
            nextCb={() => {
              if (mode === "faitTemplate") {
                void dispatch(
                  makeTemplate({
                    project: newProject.projectId,
                    thumbnail: currentThumbnail,
                    newName: newProject.newName,
                    componentId: PROJECT_LIST_COMPONENT_CLONE_LOADING_ID,
                  }),
                ).then(() => {
                  if (path[1] === "templates") {
                    navigate("/templates");
                  } else if (path[path.length - 2] === "graph") {
                    navigate(`/courses/${currentProjectGroup.id}/list/${currentProject.id}/graph`);
                  } else {
                    navigate(`/courses/${currentProjectGroup.id}/list`);
                  }
                  dispatch(
                    setAlert({
                      type: "success",
                      msg: t("pages.faitTemplate.createdSuccessfully"),
                    }),
                  );
                });
              } else if (mode === "edit-thumbnail") {
                void dispatch(
                  updateProject({
                    id: currentProject.id,
                    payload: {
                      source_thumbnail: currentThumbnail,
                      enabled_languages: editedProject.enabledLanguagesIds,
                    },
                    componentId: PROJECT_LIST_COMPONENT_ID,
                  }),
                ).then((res) => {
                  if (!updateProject.fulfilled.match(res)) {
                    return;
                  }
                  const updatedProjectElements = [...selectedProjectElements].map((element) => {
                    if (element.project?.id === res.payload.id) {
                      return {
                        ...element,
                        project: {
                          ...element.project,
                          name: res.payload.name,
                          source_thumbnail: res.payload.source_thumbnail,
                        },
                      };
                    }

                    return element;
                  });
                  dispatch(setAlert({ msg: t("alerts.projectUpdateSuccess"), type: "success" }));
                  dispatch(setCurrentElements(updatedProjectElements));
                  void dispatch(
                    fetchSpecificProjectGroupWithProjects({
                      projectGroupId: currentProjectGroup.id,
                    }),
                  );
                  void dispatch(loadGraph(currentProject.id));
                });
                navigate(`/courses/${currentProjectGroup.id}/list/${currentProject.id}/graph`);
              } else if (mode === "createProject") {
                trackEvent("project-duplicate");
                void dispatch(
                  duplicateProject({
                    project: newProject.projectId,
                    thumbnail: currentThumbnail,
                    projectGroup: newProject.projectGroupId,
                    newName: newProject.newName,
                    enabledLanguagesIds: newProject.enabledLanguagesIds,
                    componentId: PROJECT_LIST_COMPONENT_CLONE_LOADING_ID,
                  }),
                ).then((res) => {
                  if (!duplicateProject.fulfilled.match(res)) {
                    return;
                  }
                  dispatch(
                    setCurrentProject({
                      ...res.payload.data,
                      organization: res.payload.data.organization ?? currentOrganization,
                    }),
                  );
                  if (
                    Array.isArray(res.payload.data.projectGroupElement) &&
                    res.payload.data.projectGroupElement.length > 0
                  )
                    dispatch(
                      addCurrentElement({
                        id: res.payload.data.projectGroupElement[0].id,
                        order: res.payload.data.projectGroupElement[0].order,
                        project_group: currentProjectGroup,
                        project: res.payload.data,
                      }),
                    );
                  navigate(`/courses/${currentProjectGroup.id}/list/${res.payload.data.id}/graph`);
                });
              } else {
                trackEvent("project-duplicate");
                const duplicatedName = newProject.newName;
                const targetProjectGroupId =
                  Number(newProject.projectGroupId) || Number(currentProjectGroup.id);
                const prepareTarget =
                  targetProjectGroupId === Number(currentProjectGroup.id)
                    ? Promise.resolve()
                    : (() => {
                        dispatch(setSelectedProjectGroupFromProjectGroupId(targetProjectGroupId));
                        return Promise.all([
                          dispatch(
                            fetchAllCurrentElements({ projectGroupId: targetProjectGroupId }),
                          ),
                          dispatch(
                            fetchSpecificProjectGroupWithProjects({
                              projectGroupId: targetProjectGroupId,
                            }),
                          ),
                        ]).then(() => undefined);
                      })();
                void prepareTarget.then(() => {
                  navigate(`/courses/${targetProjectGroupId}/list`);
                });
                dispatch(
                  setAlert({
                    type: "success",
                    msg: t("alerts.projectDuplicationStarted"),
                    subMsg: t("alerts.projectDuplicationStartedSubMsg", { name: duplicatedName }),
                    customTimeoutInMs: 30000,
                  }),
                );
                void dispatch(
                  duplicateProject({
                    project: newProject.projectId,
                    thumbnail: currentThumbnail,
                    projectGroup: targetProjectGroupId,
                    newName: duplicatedName,
                    enabledLanguagesIds: newProject.enabledLanguagesIds,
                  }),
                ).then(async (res) => {
                  if (!duplicateProject.fulfilled.match(res)) {
                    const payload = res.payload as { error?: { message?: unknown } } | undefined;
                    dispatch(
                      setAlert({
                        type: "danger",
                        msg:
                          typeof payload?.error?.message === "string"
                            ? payload.error.message
                            : t("alerts.projectDuplicationFail", { name: duplicatedName }),
                        customTimeoutInMs: 30000,
                      }),
                    );
                    return;
                  }
                  await dispatch(
                    refreshProjectGroupIfSelected({ projectGroupId: targetProjectGroupId }),
                  );
                  dispatch(
                    setAlert({
                      type: "success",
                      msg: t("alerts.projectDuplicationSuccess", { name: duplicatedName }),
                      subMsg: t("alerts.projectDuplicationSuccessSubMsg"),
                      customTimeoutInMs: 30000,
                    }),
                  );
                });
              }
            }}
          />
        </form>
      </IsLoading>
    </>
  );
};

export default ProjectThumbnail;
